import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { ChevronDown, Loader2 } from "lucide-react";

const DRIVES = ["4WD", "2WD", "AWD"];

function Field({ label, value, onChange, options, disabled, loading, testid, placeholder }) {
  return (
    <div className="relative flex-1 min-w-[140px]">
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#00f0ff] mb-1">{label}</label>
      <div className="relative">
        <select
          data-testid={testid}
          value={value}
          disabled={disabled || loading}
          onChange={(e) => onChange(e.target.value)}
          className="w-full appearance-none bg-[#111] border-b-2 border-[#27272a] focus:border-[#00f0ff] disabled:opacity-40 text-white text-sm py-2.5 px-3 pr-8 outline-none transition-colors cursor-pointer"
        >
          <option value="">{placeholder}</option>
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        {loading
          ? <Loader2 size={15} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#00f0ff] animate-spin" />
          : <ChevronDown size={16} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />}
      </div>
    </div>
  );
}

export default function VehicleSelector({ variant = "hero", initial = {} }) {
  const [years, setYears] = useState([]);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [trims, setTrims] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [loadingTrims, setLoadingTrims] = useState(false);

  const [year, setYear] = useState(initial.year || "");
  const [make, setMake] = useState(initial.make || "");
  const [makeName, setMakeName] = useState(initial.makeName || "");
  const [model, setModel] = useState(initial.model || "");
  const [modelName, setModelName] = useState(initial.modelName || "");
  const [trim, setTrim] = useState(initial.trim || "");
  const [drive, setDrive] = useState(initial.drive || "");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/fitment/years").then((r) => setYears(r.data)).catch(() => {});
    api.get("/fitment/makes").then((r) => setMakes(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!make || !year) { setModels([]); return; }
    setLoadingModels(true);
    api.get("/fitment/models", { params: { make, year } })
      .then((r) => setModels(r.data)).catch(() => setModels([]))
      .finally(() => setLoadingModels(false));
  }, [make, year]);

  useEffect(() => {
    if (!make || !year || !model) { setTrims([]); return; }
    setLoadingTrims(true);
    api.get("/fitment/trims", { params: { make, model, year } })
      .then((r) => setTrims(r.data)).catch(() => setTrims([]))
      .finally(() => setLoadingTrims(false));
  }, [make, year, model]);

  const search = () => {
    const p = new URLSearchParams();
    if (year) p.set("year", year);
    if (make) { p.set("make", make); p.set("makeName", makeName); }
    if (model) { p.set("model", model); p.set("modelName", modelName); }
    if (trim) p.set("trim", trim);
    if (drive) p.set("drive", drive);
    navigate(`/fitment?${p.toString()}`);
  };

  const dark = variant === "hero";

  return (
    <div
      data-testid="vehicle-selector"
      className={`${dark ? "bg-[#0a0a0a]/90 backdrop-blur-xl border border-[#00f0ff]/25" : "bg-[#111] border border-[#27272a]"} p-5 md:p-6`}
    >
      <div className="text-xs uppercase tracking-[0.25em] text-zinc-300 mb-4 flex items-center gap-2">
        <span className="h-2 w-2 bttf-gradient rounded-full" /> Shop by Vehicle
        <span className="text-[#00f0ff]/70 normal-case tracking-normal text-[10px]">· powered by real OE fitment</span>
      </div>
      <div className="flex flex-wrap gap-4 items-end">
        <Field label="Year" value={year} onChange={(v) => { setYear(v); setModel(""); setModelName(""); setTrim(""); }} options={years.map((y) => ({ value: y, label: y }))} placeholder="Year" testid="fit-year" />
        <Field
          label="Make" value={make}
          onChange={(v) => { setMake(v); setMakeName(makes.find((m) => m.value === v || m.slug === v)?.name || ""); setModel(""); setModelName(""); setTrim(""); }}
          options={makes.map((m) => ({ value: m.slug, label: m.name }))}
          placeholder="Make" testid="fit-make"
        />
        <Field
          label="Model" value={model}
          onChange={(v) => { setModel(v); setModelName(models.find((m) => m.slug === v)?.name || ""); setTrim(""); }}
          options={models.map((m) => ({ value: m.slug, label: m.name }))}
          disabled={!make || !year} loading={loadingModels} placeholder="Model" testid="fit-model"
        />
        <Field label="Trim" value={trim} onChange={setTrim} options={trims.map((t) => ({ value: t, label: t }))} disabled={!model} loading={loadingTrims} placeholder="Trim" testid="fit-trim" />
        <Field label="Drive" value={drive} onChange={setDrive} options={DRIVES.map((d) => ({ value: d, label: d }))} placeholder="Drive" testid="fit-drive" />
        <button
          data-testid="fit-search-btn"
          onClick={search}
          disabled={!make || !year || !model}
          className="bttf-gradient text-black font-bold uppercase tracking-wider px-8 py-3 disabled:opacity-40 hover:opacity-90 transition-opacity whitespace-nowrap"
        >
          Find Parts
        </button>
      </div>
    </div>
  );
}
